import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';

import CustomSelect from '../../components/customFields/customSelect';

import apiCalls from '../../utils/apiCalls';
import utils from '../../utils/utils';

import GeneralForm from '../../components/generalForm';
import { connect } from 'react-redux';
import AutoResizeTextarea from '../../components/customFields/autoResizeTextarea';
import icon from '../../assets/icons/form-marketplace.svg';
import CTA_GROUP_TYPE from '../../components/constants/ctaGroupType';
import { getItems } from '../../item/itemActions';
import MultiSelect from '../../components/customFields/multiSelect';
import Helmet from 'react-helmet';
import { useCtaGroups, useCtas, useItemCollections, useMarketplaces } from './apiHooks';

const CtaGroupForm = ({ match, action, intl, history }) => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [defaultOptions, setDefaultOptions] = useState([]);
  const [ctaGroup, setCtaGroup] = useState({
    ctas: [],
    items: [],
    marketplaces: [],
    itemCollections: [],
  });

  const { isLoading: marketplacesLoading, marketplaces, error: marketplacesError } = useMarketplaces();

  const { isLoading: ctasLoading, ctas, ctasError } = useCtas();

  const { isLoading: ctaGroupsLoading, ctaGroups, error: ctaGroupsError } = useCtaGroups({ admin: true });

  const {
    isLoading: itemCollectionsLoading,
    itemCollections,
    error: itemCollectionsError,
  } = useItemCollections({ type: 'COMBO' });

  const { formatMessage } = useIntl();

  useEffect(() => {
    if (action === 'edit') {
      setLoading(true);
      setError(null);
      apiCalls
        .getCtaGroups({
          ctaGroupId: match.params.ctaGroupId,
        })
        .then((result) => {
          setCtaGroup(getCtaGroupForForm(result.data.ctaGroups[0]));
          setDefaultOptions(
            result.data.ctaGroups[0].items.map((item) => ({
              value: item.id,
              label: `${utils.getItemName(item, formatMessage)} - #${item.id}`,
            }))
          );
          setLoading(false);
        })
        .catch((errors) => {
          setError(utils.getErrorMessageFromResponse(errors));
          setLoading(false);
        });
    }
  }, [match.params.ctaGroupId]);

  const ctaGroupTypes = Object.keys(CTA_GROUP_TYPE).map((key) => {
    const type = CTA_GROUP_TYPE[key];
    return {
      value: type,
      label: intl.formatMessage({ id: 'cta.group.form.field.type.name.' + type }),
    };
  });

  const ctaOptions = ctas
    ? ctas.map((cta) => {
        return {
          value: cta.id,
          label: `${cta.title} - #${cta.id}`,
        };
      })
    : [];

  const ctaGroupOptions = ctaGroups
    ? ctaGroups.map((cg) => {
        return {
          value: cg.id,
          label: `${cg.title} - #${cg.id}`,
        };
      })
    : [];

  const marketplaceOptions = marketplaces
    ? marketplaces.map((marketplace) => {
        return {
          value: marketplace.id,
          label: `${marketplace.name} - #${marketplace.id}`,
        };
      })
    : [];

  const itemCollectionOptions = itemCollections
    ? itemCollections.map((itemCollection) => {
        return {
          value: itemCollection.id,
          label: `${itemCollection.name} - #${itemCollection.id}`,
        };
      })
    : [];

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      type: {
        type: 'string',
        title: intl.formatMessage({ id: 'cta.group.form.field.type.label' }),
        options: ctaGroupTypes,
      },
      title: {
        type: 'string',
        title: intl.formatMessage({ id: 'cta.group.form.field.title.label' }),
      },
      description: {
        type: 'string',
        title: intl.formatMessage({ id: 'cta.group.form.field.description.label' }),
        default: '',
      },
      iconSvg: {
        type: 'string',
        title: intl.formatMessage({ id: 'cta.group.form.field.icon.svg.label' }),
        default: '',
      },
      iconColor: {
        type: 'string',
        title: intl.formatMessage({ id: 'cta.group.form.field.icon.color.label' }),
        default: '',
      },
      backgroundColor: {
        type: 'string',
        title: intl.formatMessage({ id: 'cta.group.form.field.background.color.label' }),
        default: '',
      },
      isRoot: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'cta.group.form.field.is.root.label' }),
        default: '',
      },
      apiVersion: {
        type: 'string',
        title: intl.formatMessage({ id: 'cta.group.form.field.api.version.label' }),
        default: '',
      },
      items: {
        type: 'string',
        title: intl.formatMessage({ id: 'cta.group.form.field.items.label' }),
        async: true,
        loadOptions: (query, selectedItems) => {
          return apiCalls
            .getItems({ itemIds: ctaGroup ? selectedItems.join(',') : [], query, admin: true })
            .then((response) =>
              response.data.items.map((item) => {
                return {
                  value: item.id,
                  label: `${utils.getItemName(item, formatMessage)} - #${item.id}`,
                };
              })
            );
        },
        defaultOptions,
      },
      marketplaces: {
        type: 'string',
        options: marketplaceOptions,
        title: intl.formatMessage({ id: 'cta.group.form.field.marketplaces.label' }),
      },
      ctaGroups: {
        type: 'string',
        options: ctaGroupOptions,
        title: intl.formatMessage({ id: 'cta.group.form.field.cta.groups.label' }),
      },
      itemCollections: {
        type: 'string',
        options: itemCollectionOptions,
        title: intl.formatMessage({ id: 'cta.group.form.field.item.collections.label' }),
      },
      ctas: {
        type: 'string',
        options: ctaOptions,
        title: intl.formatMessage({ id: 'cta.group.form.field.ctas.label' }),
      },
      isBusinessToBusiness: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'cta.group.form.field.is.business.to.business.label' }),
      },
    },
  };

  const uiSchema = {
    type: {
      'ui:field': 'customSelect',
    },
    description: {
      'ui:field': 'autoResizeTextarea',
    },
    iconSvg: {
      'ui:help': intl.formatMessage({ id: 'cta.group.form.field.icon.svg.help' }),
    },
    ctas: {
      'ui:field': 'multiSelect',
    },
    ctaGroups: {
      'ui:field': 'multiSelect',
    },
    itemCollections: {
      'ui:field': 'multiSelect',
    },
    items: {
      'ui:field': 'multiSelect',
    },
    marketplaces: {
      'ui:field': 'multiSelect',
    },
  };

  function onFormSubmit() {
    setLoading(true);

    if (action === 'edit') {
      return apiCalls
        .editCtaGroup({ ctaGroup })
        .then((result) => {
          setLoading(false);
          history.push({
            pathname: '/admin/ctaGroups',
          });
        })
        .catch((error) => {
          setLoading(false);
          setError(utils.getErrorMessageFromResponse(error, true));
          window.scrollTo(0, 0);
        });
    } else {
      return apiCalls
        .createCtaGroup({ ctaGroup })
        .then((result) => {
          setLoading(false);
          history.push({
            pathname: '/admin/ctaGroups',
          });
        })
        .catch((error) => {
          setLoading(false);
          setError(utils.getErrorMessageFromResponse(error, true));
          window.scrollTo(0, 0);
        });
    }
  }

  const buttonCopy = action === 'edit' ? 'cta.group.edit.form.submit' : 'cta.group.create.form.submit';

  const formFields = {
    customSelect: CustomSelect,
    multiSelect: MultiSelect,
    autoResizeTextarea: AutoResizeTextarea,
  };

  const header =
    action === 'edit' && !loading
      ? intl.formatMessage({ id: 'cta.group.edit.header' }, { ctaGroupTitle: ctaGroup.title })
      : intl.formatMessage({ id: 'cta.group.create.header' });

  return (
    <div className="row">
      <Helmet>
        <title>{header}</title>
      </Helmet>

      <div className="col-12 col-lg-10 offset-lg-1 admin__form">
        <GeneralForm
          title={header}
          icon={icon}
          onChange={(event) => setCtaGroup(event.formData)}
          onSubmit={onFormSubmit}
          formSchema={formSchema}
          formUiSchema={uiSchema}
          formFields={formFields}
          formData={ctaGroup}
          buttonCopy={buttonCopy}
          errorMessage={error || itemCollectionsError || ctasError || ctaGroupsError || marketplacesError}
          loading={loading || itemCollectionsLoading || ctasLoading || ctaGroupsLoading || marketplacesLoading}
          hideImageUpload={true}
        />
      </div>
    </div>
  );
};

function getCtaGroupForForm(ctaGroup) {
  const ctaIds = ctaGroup.ctas.map((cta) => cta.id);
  const ctaGroupIds = ctaGroup.ctaGroups.map((cg) => cg.id);
  const itemIds = ctaGroup.items.map((i) => i.id);
  const marketplaceIds = ctaGroup.marketplaces.map((m) => m.id);
  const itemCollectionIds = ctaGroup.itemCollections.map((m) => m.id);
  const result = Object.assign({}, ctaGroup);
  result.ctas = ctaIds;
  result.ctaGroups = ctaGroupIds;
  result.items = itemIds;
  result.marketplaces = marketplaceIds;
  result.itemCollections = itemCollectionIds;
  return result;
}

CtaGroupForm.propTypes = {
  currentUser: PropTypes.object,
  action: PropTypes.string.isRequired,
};

CtaGroupForm.defaultProps = {
  action: 'create',
};

const mapDispatchToProps = {
  getItems,
};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
    items: state.item.items,
  };
}

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(CtaGroupForm));
